import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../hooks/use-toast';
import { RealtimeChat } from '../../../utils/RealtimeAudio';
import { Phone, PhoneOff, X, Send, Mic, MessageCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Toaster } from '../../ui/toaster';
import { supabaseReservation } from '../../../integrations/supabase/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

type CommunicationMode = 'voice' | 'chat' | 'both';
type ActiveTab = 'voice' | 'chat';

const Embed = () => {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [status, setStatus] = useState<'ready' | 'connecting' | 'listening' | 'speaking'>('ready');
  const [messages, setMessages] = useState<Message[]>([]);
  const [communicationMode, setCommunicationMode] = useState<CommunicationMode>('voice');
  const [activeTab, setActiveTab] = useState<ActiveTab>('voice');
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const chatRef = useRef<RealtimeChat | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get URL params for customization
  const params = new URLSearchParams(window.location.search);
  const accentColor = params.get('accent') || '#0A84FF';
  const titleParam = params.get('title');
  const subtitleParam = params.get('subtitle');
  const accountId = params.get('account_id') || null;

  // Fetch communication mode from agent_config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        let query = supabaseReservation.from('agent_config').select('communication_mode');
        if (accountId) {
          query = query.eq('account_id', accountId);
        }
        const { data } = await query.limit(1).maybeSingle();
        if (data?.communication_mode) {
          const mode = data.communication_mode as CommunicationMode;
          setCommunicationMode(mode);
          if (mode === 'chat') {
            setActiveTab('chat');
          } else {
            setActiveTab('voice');
          }
        }
        setConfigLoaded(true);
      } catch (error) {
        console.error('Error fetching config:', error);
        setConfigLoaded(true);
      }
    };
    fetchConfig();
  }, [accountId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const extractAssistantText = (event: any): string | null => {
    const direct = event?.text ?? event?.output_text ?? event?.transcript;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    const output = event?.response?.output;
    if (Array.isArray(output)) {
      const parts: string[] = [];
      for (const item of output) {
        const content = item?.content;
        if (!Array.isArray(content)) continue;
        for (const c of content) {
          if (typeof c?.text === 'string') parts.push(c.text);
          if (typeof c?.transcript === 'string') parts.push(c.transcript);
        }
      }
      const joined = parts.join('').trim();
      return joined || null;
    }

    return null;
  };

  const handleMessage = (event: Record<string, unknown>) => {
    const isTextOnlyActive = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');

    if (!isTextOnlyActive) {
      if (event.type === 'response.audio.delta') {
        setIsSpeaking(true);
        setStatus('speaking');
        return;
      }
      if (event.type === 'response.audio.done') {
        setIsSpeaking(false);
        setStatus('listening');
        return;
      }
      if (event.type === 'input_audio_buffer.speech_started') {
        setStatus('listening');
        return;
      }
      if (event.type === 'response.audio_transcript.done') {
        const transcript = (event as { transcript?: string }).transcript;
        if (transcript) {
          setMessages(prev => [...prev, { role: 'assistant', content: transcript }]);
        }
        return;
      }
      if (event.type === 'conversation.item.input_audio_transcription.completed') {
        const transcript = (event as { transcript?: string }).transcript;
        if (transcript) {
          setMessages(prev => [...prev, { role: 'user', content: transcript }]);
        }
        return;
      }
    }

    if (
      event.type === 'response.text.done' ||
      event.type === 'response.output_text.done' ||
      event.type === 'response.done'
    ) {
      const text = extractAssistantText(event);
      if (text) {
        setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
      }
      setIsSending(false);
      if (isTextOnlyActive) setStatus('ready');
      return;
    }
  };

  const startConversation = async () => {
    setIsConnecting(true);
    setStatus('connecting');

    // Determine if we should use text-only mode (no microphone)
    const useTextOnly = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');

    try {
      chatRef.current = new RealtimeChat(handleMessage, undefined, accountId, useTextOnly);
      await chatRef.current.init();
      setIsConnected(true);
      setStatus(useTextOnly ? 'ready' : 'listening');

      toast({
        title: "Connected",
        description: useTextOnly ? "Type your message" : "Start speaking now!",
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
      setStatus('ready');
      toast({
        title: "Connection Failed",
        description: error instanceof Error ? error.message : 'Failed to connect',
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const endConversation = async () => {
    await chatRef.current?.disconnect();
    setIsConnected(false);
    setIsSpeaking(false);
    setIsSending(false);
    setStatus('ready');
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !isConnected) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);

    try {
      chatRef.current?.sendText(userMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsSending(false);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  useEffect(() => {
    return () => {
      chatRef.current?.disconnect();
    };
  }, []);

  const statusText = {
    ready: 'Tap to start',
    connecting: 'Connecting...',
    listening: 'Listening...',
    speaking: 'Speaking...',
  };

  if (!configLoaded) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#0A0A0A', color: '#FAFAFA' }}
      >
        <div className="animate-pulse text-[#666]">Loading...</div>
      </div>
    );
  }

  const showTabs = communicationMode === 'both';
  const showVoice = communicationMode === 'voice' || (communicationMode === 'both' && activeTab === 'voice');
  const showChat = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');

  const defaultTitle = communicationMode === 'chat' ? 'Chat Assistant' : communicationMode === 'both' ? 'Assistant' : 'Voice Assistant';
  const displayTitle = titleParam || defaultTitle;
  const defaultSubtitle = communicationMode === 'chat' ? 'Type your message below' : 'Tap to start speaking';
  const displaySubtitle = subtitleParam || defaultSubtitle;

  return (
    <div className="reservation-main">
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        '--embed-accent': accentColor,
        backgroundColor: '#0A0A0A',
        color: '#FAFAFA'
      } as React.CSSProperties}
    >
      <Toaster />
      
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#333]">
        <div>
          <h1 className="font-semibold text-sm">{displayTitle}</h1>
          <p className="text-xs text-[#999]">{displaySubtitle}</p>
        </div>
        {isConnected && (
          <button
            onClick={endConversation}
            className="p-2 rounded-full hover:bg-[#222] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </header>

      {/* Tabs for "both" mode */}
      {showTabs && (
        <div className="flex border-b border-[#333]">
          <button
            onClick={() => {
              if (isConnected) endConversation();
              setActiveTab('voice');
            }}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors",
              activeTab === 'voice' 
                ? "text-white border-b-2" 
                : "text-[#666] hover:text-[#999]"
            )}
            style={{ borderColor: activeTab === 'voice' ? accentColor : 'transparent' }}
          >
            <Mic className="h-4 w-4" />
            Voice
          </button>
          <button
            onClick={() => {
              if (isConnected) endConversation();
              setActiveTab('chat');
            }}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors",
              activeTab === 'chat' 
                ? "text-white border-b-2" 
                : "text-[#666] hover:text-[#999]"
            )}
            style={{ borderColor: activeTab === 'chat' ? accentColor : 'transparent' }}
          >
            <MessageCircle className="h-4 w-4" />
            Chat
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && !isConnected && (
          <div className="text-center text-[#666] text-sm py-8">
            {showVoice ? (
              <p>Click the button below to start a voice conversation</p>
            ) : (
              <p>Click the button below to start chatting</p>
            )}
          </div>
        )}
        {messages.map((message, index) => (
          <div
            key={index}
            className={cn(
              "flex",
              message.role === 'user' ? "justify-end" : "justify-start"
            )}
          >
            <div
              className={cn(
                "rounded-2xl px-4 py-2 max-w-[85%] text-sm",
                message.role === 'user'
                  ? "rounded-br-md"
                  : "bg-[#1A1A1A] rounded-bl-md"
              )}
              style={{
                backgroundColor: message.role === 'user' ? accentColor : '#1A1A1A',
                color: message.role === 'user' ? '#0A0A0A' : '#FAFAFA'
              }}
            >
              {message.content}
            </div>
          </div>
        ))}
        {isSending && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-md px-4 py-2 text-sm bg-[#1A1A1A]">
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-[#666] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-[#666] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-[#666] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Voice Button */}
      {showVoice && (
        <div className="p-4 flex flex-col items-center gap-2 border-t border-[#333]">
          {/* Audio Visualizer */}
          {isConnected && (
            <div className="flex items-end gap-1 h-8 mb-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 rounded-full transition-all duration-100"
                  style={{
                    backgroundColor: accentColor,
                    height: isSpeaking ? `${Math.random() * 24 + 8}px` : '6px',
                    animation: isSpeaking ? `audioWave 0.5s ease-in-out infinite ${i * 0.1}s` : 'none'
                  }}
                />
              ))}
            </div>
          )}

          <button
            onClick={isConnected ? endConversation : startConversation}
            disabled={isConnecting}
            className={cn(
              "relative flex items-center justify-center w-16 h-16 rounded-full transition-all duration-300",
              isConnecting && "opacity-50 cursor-not-allowed"
            )}
            style={{
              backgroundColor: isConnected ? accentColor : '#1A1A1A',
              boxShadow: isConnected ? `0 0 20px ${accentColor}66` : 'none'
            }}
          >
            {isConnected ? (
              <PhoneOff className="h-6 w-6" style={{ color: '#0A0A0A' }} />
            ) : (
              <Phone className="h-6 w-6" style={{ color: '#666' }} />
            )}
            
            {isSpeaking && (
              <div 
                className="absolute inset-0 rounded-full animate-ping opacity-30"
                style={{ backgroundColor: accentColor }}
              />
            )}
          </button>

          <span className="text-xs text-[#999]">{statusText[status]}</span>
        </div>
      )}

      {/* Chat Input */}
      {showChat && (
        <div className="p-4 border-t border-[#333]">
          {!isConnected ? (
            <button
              onClick={startConversation}
              disabled={isConnecting}
              className={cn(
                "w-full py-3 rounded-lg font-medium text-sm transition-all",
                isConnecting && "opacity-50"
              )}
              style={{ backgroundColor: accentColor, color: '#0A0A0A' }}
            >
              {isConnecting ? 'Connecting...' : 'Start Chat'}
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 rounded-lg text-sm text-white placeholder-[#666] bg-[#1A1A1A] focus:outline-none focus:ring-1"
                style={{ borderColor: accentColor }}
                disabled={isSending}
              />
              <button
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || isSending}
                className={cn(
                  "px-4 py-2 rounded-lg transition-all",
                  (!chatInput.trim() || isSending) && "opacity-50"
                )}
                style={{ backgroundColor: accentColor }}
              >
                <Send className="h-4 w-4" style={{ color: '#0A0A0A' }} />
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes audioWave {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
    </div>
  );
};

export default Embed;